import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { AnimatePresence } from "motion/react"
import { useTranslation } from "react-i18next"

import type { ActivityMessage, Document, ToolApproval } from "@/api/models"

import { MemoryCandidate } from "@/components/MemoryCandidate/MemoryCandidate"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { buttonVariants } from "@/components/ui/button-variants"
import { useFeatureEnabled } from "@/hooks/use-feature-enabled"
import { usePrismMemory } from "@/hooks/use-prism-memory"
import { cn } from "@/lib/utils"
import {
  translateActivityGroupTitle,
  translateActivityMessage,
} from "@/utils/activity-message-translator"

import { ActiveAgents } from "./active-agents"
import { ActivityMessageGroup } from "./activity-message-group"
import { ActivityMessageHeader } from "./activity-message-header"
import { ActivityMessageItem } from "./activity-message-item"
import { getActivityIcon } from "./activity-message.utils"
import { ActivityNavigator } from "./activity-navigator"
import { ActivityStatsHeader } from "./activity-stats-header"
import { useActivityMessageScroll } from "../../hooks/use-activity-message-scroll"
import {
  extractTruncatedActivitySentinel,
  filterSystemNoise,
  getLatestActivity,
  getLatestTokenInfo,
  getRoutedModelInfo,
  getSourcesByType,
  getTotalSourcesCount,
  groupActivityMessages,
} from "../../utils/activity-message-utils"
import { getFilthCitationKeys, getPrismFilthSearchState } from "../../utils/filth-search-activity"
import { PrismRetrieval } from "../prism-retrieval/prism-retrieval"
import { getPrismCitations } from "../prism-retrieval/prism-retrieval-utils"
import { TreeGraphPanel } from "../TreeGraphPanel"

const SCROLL_THRESHOLD = 20 // pixels from bottom to consider "at bottom"

export type ActivityMessageHistoryProps = {
  messages: ActivityMessage[]
  isDefaultOpen?: boolean
  id?: string
  citationSection?: string
  isLoading?: boolean
  demoMode?: boolean
  toolApprovals?: ToolApproval[]
  /** Override the masked-loading placeholder (default "Thinking…"). Used by
   *  Deep Explorer to show "Awaiting plan approval" while the run is paused
   *  on the plan-approval interrupt — the default placeholder reads as if
   *  the model is stuck. */
  loadingPlaceholder?: string
  /** This turn's own answer citations, the only source of document names in
   *  Prism (#3261). Search-result activities are never read for names. */
  citations?: string[]
  /** Library documents of this turn, used only to resolve a cited alias to
   *  its display name and to tell FILTH citations from the rest. */
  citationDocuments?: [string, Document][]
  /** The answer's own citation click handler (0-based citation index), so a
   *  Prism chip does exactly what clicking that superscript does. */
  onCitationClicked?: (citationIndex: number) => void
  /** Brings the answer's citation list into view at a citation index. Pass it
   *  only while that list is rendered. */
  onOtherCitationsClicked?: (citationIndex: number) => void
  /** Identifies this turn to Prism across a remount of its message. */
  prismTurnKey?: string
  /** The answer is written but the documents behind its library citations
   *  have not arrived yet; Prism stays live to name them. */
  prismAwaitingSources?: boolean
}

export function ActivityMessageHistory({
  messages: rawMessages,
  isDefaultOpen = false,
  id = "activity-message-history",
  citationSection,
  isLoading = false,
  demoMode = false,
  toolApprovals,
  loadingPlaceholder,
  citations,
  citationDocuments,
  onCitationClicked,
  onOtherCitationsClicked,
  prismTurnKey,
  prismAwaitingSources = false,
}: ActivityMessageHistoryProps) {
  const { t } = useTranslation()
  const prismMemory = usePrismMemory()
  // Durable Explorer's live snapshot may inject a `truncated` sentinel when
  // it elided older activities to fit its byte budget (see
  // `ACTIVITY_TRUNCATED_TYPE` in `activity-message-utils.ts`). Pull it out
  // before anything below treats it as a real activity — it has no
  // `activity_type`-specific icon/translation, no timestamp worth grouping
  // by, and renders instead as its own banner, mirroring Deep Explorer's
  // `{ event: "truncated", dropped: n }` handling in `ActivityLog.tsx`.
  const { sentinel: truncatedSentinel, rest: messages } = useMemo(
    () => extractTruncatedActivitySentinel(rawMessages),
    [rawMessages]
  )
  const [isAccordionOpen, setIsAccordionOpen] = useState(isDefaultOpen)
  const singleColumnScrollRef = useRef<HTMLDivElement | null>(null)
  const lastMessageCountRef = useRef(0)
  const [isUserScrolling, setIsUserScrolling] = useState(false)
  const prevHasGroupTitleRef = useRef(false)
  const prevIsDefaultOpenRef = useRef(isDefaultOpen)
  const [hasGroupTitle, setHasGroupTitle] = useState(false)

  useEffect(() => {
    if (hasGroupTitle) {
      return
    }

    if (messages.some((activity) => Boolean(activity.details?.group_title))) {
      setHasGroupTitle(true)
    }
  }, [hasGroupTitle, messages])

  // Expand automatically when research activity begins
  useEffect(() => {
    const shouldOpenFromDefault = !prevIsDefaultOpenRef.current && isDefaultOpen
    const shouldOpenFromGroupTitle = !prevHasGroupTitleRef.current && hasGroupTitle

    if ((shouldOpenFromDefault || shouldOpenFromGroupTitle) && !isAccordionOpen) {
      setIsAccordionOpen(true)
    }

    prevHasGroupTitleRef.current = hasGroupTitle
    prevIsDefaultOpenRef.current = isDefaultOpen
  }, [hasGroupTitle, isDefaultOpen, isAccordionOpen])

  // Filter out system bookkeeping (preparing_context, processing_request, etc.)
  // from the user-facing thinking phase. tokenInfo still uses raw `messages`
  // below in case token data rides on a filtered activity.
  const isTreeRetrievalEnabled = useFeatureEnabled("tree_retrieval")
  const isFilthVisualizationEnabled = useFeatureEnabled("filth_search_visualization")
  const filthSearchState = useMemo(() => getPrismFilthSearchState(messages), [messages])
  // The keys tell the answer's FILTH citations apart while it streams, before
  // its documents arrive with the saved message; they are never rendered.
  const filthCitationKeys = useMemo(() => getFilthCitationKeys(messages), [messages])
  const prismCitations = useMemo(
    () => getPrismCitations(citations, citationDocuments, filthCitationKeys),
    [citations, citationDocuments, filthCitationKeys]
  )
  // Whether a citation that is not FILTH is known to be another source yet.
  const prismOtherSourcesKnown =
    (citationDocuments?.length ?? 0) > 0 || filthCitationKeys !== undefined
  const firstOtherCitationIndex = prismCitations.otherCitationIndexes.at(0)
  const handlePrismOtherSourcesClick = useMemo(
    () =>
      onOtherCitationsClicked && firstOtherCitationIndex !== undefined
        ? () => onOtherCitationsClicked(firstOtherCitationIndex)
        : undefined,
    [firstOtherCitationIndex, onOtherCitationsClicked]
  )
  const graphMessages = useMemo(
    () =>
      isTreeRetrievalEnabled
        ? messages.filter((activity) => activity.activity_type === "thorough_search_graph")
        : [],
    [messages, isTreeRetrievalEnabled]
  )
  const standardMessages = useMemo(
    () => messages.filter((activity) => activity.activity_type !== "thorough_search_graph"),
    [messages]
  )
  const filteredMessages = useMemo(() => filterSystemNoise(standardMessages), [standardMessages])

  const latestActivity = getLatestActivity(filteredMessages)
  const latestActivityTextBase = latestActivity
    ? translateActivityMessage(latestActivity, t)
    : undefined

  // TTFT masking: loading but no real activity has arrived yet.
  // Show a single static phrase (rendered immediately without text animation).
  const isMasking = isLoading && !latestActivity

  const latestActivityText = useMemo(() => {
    if (latestActivityTextBase) return latestActivityTextBase

    // No activities yet but loading — single static placeholder. Callers
    // (e.g. Deep Explorer's plan-approval pause) can override the default.
    if (isLoading) {
      if (loadingPlaceholder) return loadingPlaceholder
      return t("activity_messages.thinking", { defaultValue: "Thinking..." })
    }

    return undefined
  }, [isLoading, latestActivityTextBase, loadingPlaceholder, t])

  const activeIcon = latestActivity ? getActivityIcon(latestActivity) : undefined

  const totalActivities = filteredMessages.length
  const tokenInfo = useMemo(() => getLatestTokenInfo(messages), [messages])
  const sourcesCount = useMemo(() => getTotalSourcesCount(filteredMessages), [filteredMessages])
  const sourcesByType = useMemo(() => getSourcesByType(filteredMessages), [filteredMessages])
  const routedModelInfo = useMemo(() => getRoutedModelInfo(filteredMessages), [filteredMessages])
  const groupedMessages = groupActivityMessages(
    filteredMessages,
    t("activity_messages.other_activities", "Other Activities"),
    (groupTitle) => translateActivityGroupTitle(groupTitle, t)
  )
  const hasCustomGroups = groupedMessages.length > 0

  const { scrollToSection, activeSection } = useActivityMessageScroll({
    isAccordionOpen,
    groupedMessages,
  })

  const isAtBottom = useCallback((container: HTMLElement) => {
    const { scrollTop, scrollHeight, clientHeight } = container
    return scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD
  }, [])

  // Auto-scroll only counts standard activity items in the scroll container.
  // Graph panel renders outside the container, so including graphMessages.length
  // would trigger scroll on graph events when no new scrollable content exists.
  const renderedMessageCount = filteredMessages.length
  useEffect(() => {
    if (!isAccordionOpen || hasCustomGroups || !singleColumnScrollRef.current || isUserScrolling) {
      return
    }

    const container = singleColumnScrollRef.current
    const isFirstOpen = lastMessageCountRef.current === 0 && renderedMessageCount > 0
    const hasNewMessages =
      renderedMessageCount > lastMessageCountRef.current && lastMessageCountRef.current > 0

    if (isFirstOpen || hasNewMessages) {
      setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: isFirstOpen ? "auto" : "smooth",
        })
      }, 100)
    }

    lastMessageCountRef.current = renderedMessageCount
  }, [renderedMessageCount, isAccordionOpen, hasCustomGroups, isUserScrolling])

  useEffect(() => {
    if (!isAccordionOpen || hasCustomGroups || !singleColumnScrollRef.current) return

    const container = singleColumnScrollRef.current
    let lastHeight = container.scrollHeight

    const handleResize = () => {
      const { scrollTop, clientHeight, scrollHeight } = container
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD
      if (scrollHeight > lastHeight && isAtBottom && !isUserScrolling) {
        container.scrollTo({ top: container.scrollHeight, behavior: "auto" })
      }
      lastHeight = scrollHeight
    }

    const observer = new ResizeObserver(handleResize)
    observer.observe(container)

    return () => observer.disconnect()
  }, [isAccordionOpen, hasCustomGroups, isUserScrolling])

  const handleSingleColumnScroll = useCallback(() => {
    if (!singleColumnScrollRef.current) return

    const container = singleColumnScrollRef.current
    const atBottom = isAtBottom(container)

    if (!atBottom) {
      setIsUserScrolling(true)
    } else {
      setIsUserScrolling(false)
    }
  }, [isAtBottom])

  let currentGroupTitle: string | undefined

  if (groupedMessages.length > 0) {
    currentGroupTitle = groupedMessages[groupedMessages.length - 1].title
  } else if (latestActivity?.details?.group_title) {
    currentGroupTitle = translateActivityGroupTitle(latestActivity.details.group_title, t)
  }

  const accordionValue = isAccordionOpen ? id : ""

  const pendingMemorySuggestions = useMemo(() => {
    const seen = new Set<string>()
    const texts: string[] = []
    for (const activity of messages) {
      if (activity.activity_type !== "memory_saved" || activity.details?.status !== "suggested") {
        continue
      }
      const text =
        typeof activity.details.text === "string" ? (activity.details.text as string).trim() : ""
      if (!text || seen.has(text)) continue
      seen.add(text)
      texts.push(text)
    }
    return texts
  }, [messages])

  return (
    <div className="flex flex-col gap-3">
      {isFilthVisualizationEnabled && filthSearchState && (
        <PrismRetrieval
          awaitingSources={prismAwaitingSources}
          isLive={isLoading}
          otherCitedCount={prismCitations.otherCitationIndexes.length}
          otherSourcesKnown={prismOtherSourcesKnown}
          sources={prismCitations.sources}
          state={filthSearchState}
          turnKey={prismTurnKey}
          onOtherSourcesClick={handlePrismOtherSourcesClick}
          onSourceClick={onCitationClicked}
        />
      )}
      <Accordion
        collapsible
        type="single"
        value={accordionValue}
        onValueChange={(value) => setIsAccordionOpen(value === id)}
      >
        <AccordionItem
          className="bg-card/50 w-full rounded-lg border px-4 py-3 backdrop-blur-sm"
          value={id}
        >
          <AccordionTrigger
            className={cn(
              "text-base hover:no-underline",
              buttonVariants({
                variant: "icon",
                className:
                  "hover:text-foreground w-full px-0 hover:bg-transparent hover:no-underline",
              })
            )}
          >
            <ActivityMessageHeader
              activeIcon={activeIcon}
              currentGroupTitle={currentGroupTitle}
              isLoading={isLoading}
              isMasking={isMasking}
              latestActivityText={latestActivityText}
              routedModelInfo={routedModelInfo}
              sourcesByType={sourcesByType}
              totalActivities={totalActivities}
            />
          </AccordionTrigger>
          <AccordionContent className="flex h-full w-full flex-col pb-0">
            {truncatedSentinel && (
              <p
                key="activity-truncated-sentinel"
                className="text-muted-foreground/80 py-1 text-center text-[10px] italic"
                role="note"
              >
                {t("activity_messages.truncated", {
                  count: Number(truncatedSentinel.details?.dropped ?? 0),
                  defaultValue: "{{count}} earlier activity omitted",
                })}
              </p>
            )}
            {hasCustomGroups ? (
              <div className="flex flex-col gap-6">
                <section className="mt-4 grid grid-cols-1 gap-4">
                  <ActivityStatsHeader sourcesCount={sourcesCount} tokenInfo={tokenInfo} />
                  <ActiveAgents
                    activeSection={activeSection}
                    groupedMessages={groupedMessages}
                    isLoading={isLoading}
                  />
                </section>
                <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-[213px_1fr]">
                  <div className="shrink-0">
                    <ActivityNavigator
                      activeSection={activeSection}
                      isLoading={isLoading}
                      messages={groupedMessages}
                      onSectionClick={scrollToSection}
                    />
                  </div>
                  <div className="min-w-0">
                    <div
                      data-scroll-container
                      className="hide-scrollbar flex max-h-[500px] flex-col gap-6 overflow-y-auto"
                    >
                      <AnimatePresence>
                        {groupedMessages.map((group, groupIndex) => (
                          <ActivityMessageGroup
                            key={groupIndex}
                            citationSection={citationSection}
                            demoMode={demoMode}
                            group={group}
                            groupIndex={groupIndex}
                            isLast={groupIndex === groupedMessages.length - 1}
                            toolApprovals={toolApprovals}
                          />
                        ))}
                      </AnimatePresence>
                      {graphMessages.length > 0 && <TreeGraphPanel messages={graphMessages} />}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="my-6 grid w-full grid-cols-1 gap-6">
                <div className="relative min-w-0">
                  <div className="from-background pointer-events-none absolute top-0 left-0 z-10 h-6 w-full bg-linear-to-b to-transparent" />
                  <div
                    ref={singleColumnScrollRef}
                    className="hide-scrollbar grid max-h-[500px] w-full grid-cols-1 gap-6 overflow-y-auto px-4"
                    onScroll={handleSingleColumnScroll}
                  >
                    <AnimatePresence>
                      {filteredMessages.map((activity, index) => (
                        <ActivityMessageItem
                          key={index}
                          activity={activity}
                          activityIndex={index}
                          citationSection={citationSection}
                          demoMode={demoMode}
                          toolApprovals={toolApprovals}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
                {graphMessages.length > 0 && <TreeGraphPanel messages={graphMessages} />}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {!prismMemory && pendingMemorySuggestions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {pendingMemorySuggestions.map((text) => (
            <MemoryCandidate key={text} text={text} />
          ))}
        </div>
      )}
    </div>
  )
}
